import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from 'types';
import { generateFormData } from 'utils';

import { apiBaseQuery } from 'modules/apiBaseQuery';

import {
  ChangePasswordPayload,
  EnableTwoFaAppResponse,
  IUser,
  LoginPayload,
  LoginResponse,
  ResetPasswordPayload,
  ResetTwoFaPayload,
  SetNewPasswordPayload,
  SignupPayload,
  VerifyOtpPayload,
} from '../types/auth-session.types';

export const authSessionApi = createApi({
  reducerPath: 'authSessionApi',
  baseQuery: apiBaseQuery(`${process.env.REACT_APP_API_URL}`),
  endpoints: (builder) => ({
    getCSRFCookie: builder.query<void, void>({
      query: () => ({
        url: '/v2/sanctum/csrf-cookie',
        method: 'GET',
      }),
    }),

    login: builder.mutation<ApiResponse<LoginResponse>, LoginPayload>({
      query: (payload) => ({
        url: '/v2/login',
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),

    logout: builder.query<void, void>({
      query: () => ({
        url: '/v2/logout',
        method: 'POST',
      }),
    }),

    resetPassword: builder.mutation<ApiResponse<void>, ResetPasswordPayload>({
      query: (payload) => ({
        url: '/v2/forgot-password',
        method: 'POST',
        body: payload,
      }),
    }),

    setNewPassword: builder.mutation<ApiResponse<void>, SetNewPasswordPayload>({
      query: (payload) => ({
        url: '/v2/reset-password',
        method: 'POST',
        body: payload,
      }),
    }),

    checkSession: builder.query<ApiResponse<IUser>, void>({
      query: () => ({
        url: '/v2/api/me',
        method: 'GET',
      }),
    }),

    signup: builder.mutation<ApiResponse<IUser>, SignupPayload>({
      query: (payload) => ({
        url: '/v2/signup',
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),

    verifyOtp: builder.mutation<ApiResponse<any>, VerifyOtpPayload>({
      query: (payload) => ({
        url: '/v2/2fa/verify',
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),

    verifyAppOtp: builder.mutation<ApiResponse<void>, VerifyOtpPayload>({
      query: (payload) => ({
        url: '/v2/2fa/google/verify',
        method: 'POST',
        body: payload,
      }),
    }),

    enableTwoFaApp: builder.query<ApiResponse<EnableTwoFaAppResponse>, void>({
      query: () => ({
        url: '/v2/2fa/google/enable',
        method: 'GET',
      }),
    }),

    confirmEnableTwoFaApp: builder.mutation<ApiResponse<EnableTwoFaAppResponse>, VerifyOtpPayload>({
      query: (payload) => ({
        url: '/v2/2fa/google/confirm',
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),

    disableTwoFaApp: builder.mutation<ApiResponse<any>, VerifyOtpPayload>({
      query: (payload) => ({
        url: '/v2/2fa/email/request-switch',
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),

    resetTwoFaApp: builder.query<ApiResponse<any>, ResetTwoFaPayload>({
      query: (payload) => ({
        url: `/v2/2fa/email/enable/${payload.user_id}`,
        method: 'GET',
      }),
    }),

    confirmEnableTwoFaEmail: builder.mutation<ApiResponse<any>, VerifyOtpPayload>({
      query: (payload) => ({
        url: '/v2/2fa/email/confirm-switch',
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),
    changePasswordVerifyOtp: builder.mutation<ApiResponse<void>, VerifyOtpPayload>({
      query: (payload) => ({
        url: '/v2/api/me/password/change/verify-2fa',
        method: 'POST',
        body: payload,
      }),
    }),
    changePassword: builder.mutation<ApiResponse<void>, ChangePasswordPayload>({
      query: (payload) => ({
        url: '/v2/api/me/password/change',
        method: 'POST',
        body: payload,
      }),
    }),
    changePasswordRequest: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/v2/api/me/password/change/request',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useLazyCheckSessionQuery,
  useLazyGetCSRFCookieQuery,
  useLazyLogoutQuery,
  useLoginMutation,
  useSignupMutation,
  useConfirmEnableTwoFaEmailMutation,
  useConfirmEnableTwoFaAppMutation,
  useDisableTwoFaAppMutation,
  useVerifyOtpMutation,
  useLazyEnableTwoFaAppQuery,
  useLazyResetTwoFaAppQuery,
  useVerifyAppOtpMutation,
  useResetPasswordMutation,
  useSetNewPasswordMutation,
  useChangePasswordVerifyOtpMutation,
  useChangePasswordMutation,
  useChangePasswordRequestMutation,
} = authSessionApi;
